import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {combineLatest, Observable, of, Subject} from "rxjs";
import {FileService} from "../services/file.service";
import {catchError, map, mergeMap, switchMap, take, tap} from "rxjs/operators";

import {saveAs as importedSaveAs} from "file-saver";

import Swal from 'sweetalert2';
import {ActivatedRoute} from "@angular/router";

import {AppConsts} from "../appConsts";
import {DropDownFilterSettings} from "@progress/kendo-angular-dropdowns";
import {State} from "@progress/kendo-data-query";
import {DataStateChangeEvent} from "@progress/kendo-angular-grid";

@Component({
  selector: 'app-file-manager-shell',
  templateUrl: './file-manager-shell.component.html',
  styleUrls: ['./file-manager-shell.component.css'],
  encapsulation: ViewEncapsulation.Emulated
})
export class FileManagerShellComponent implements OnInit {
  public distinctCategories: any[];
  public hardReloadSelected: boolean;
  public GuidedownloadUrl:string;

  constructor(public fileService: FileService, private route: ActivatedRoute) {
  }

  autoCompleteLoading: any;
  gridDataLoading: any;
  gridData: any = [];

  public buttonCount = 5;
  public info = true;
  public type: 'numeric' | 'input' = 'numeric';
  public pageSizes = true;
  public previousNext = true;

  public defaultState: State = {
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: []
    }
  };
  public state: State = {...this.defaultState};

  folderSelected: Subject<any> = new Subject<any>();
  folderSelected$: Observable<any> = this.folderSelected.asObservable();

  folderSuggestions$: Observable<any>;

  autoCompleteSubject: Subject<any> = new Subject<any>();
  autoComplete$ = this.autoCompleteSubject.asObservable();

  autoCompleteValueSubject: Subject<any> = new Subject<any>();
  autoCompleteValue$: Observable<any> = this.autoCompleteValueSubject.asObservable();

  public items: any[] = [];

  filterApplied: boolean = false;
  folderClicked: boolean = false;
  isDownloading: any;
  isAzureFiles: boolean = false;
  pdf: any;

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
    fields: ['name']
  };
  selectedFolderSuggestion: any = {};

  private applyFilterAnimation(field): void {
    this[field] = true;
    setTimeout(() => this[field] = false, 1500);
  }

  removeHash() {
    history.pushState("", document.title, window.location.pathname
      + window.location.search);
  }

  ngOnInit() {
    this.GuidedownloadUrl = AppConsts.GuidedownloadUrl;
    this.removeHash();
    this.folderSuggestions$ = combineLatest(this.fileService.getDistributions(), this.autoComplete$, (distributionsResponse, autoCompleteStr) => ({
      distributions: distributionsResponse.result,
      selectedFolder: autoCompleteStr
    }))
      .pipe(tap(() => this.autoCompleteLoading = true), switchMap(({selectedFolder, distributions}) => {
        if(AppConsts.JobFilesDataSource === 1){
          this.isAzureFiles = true;
        }
        const distFilter = distributions
          .filter((dist) => dist.distributorName.toUpperCase().includes(selectedFolder.toUpperCase()))
          .map((distName) => ({
            ...distName,
            jobNumber: distName.distributorNumber,
            formattedName: distName.distributorName,
            formattedDistNum: distName.distributorNumber,
            getFromDb: true
          }));
        return selectedFolder ? this.fileService.getSuggestionsForAutocomplete(selectedFolder).pipe(map(({result}) => ({
          result,
          distributions: distFilter
        }))) : of({result: [], distributions: []});
      }), map((response) => {
        this.autoCompleteLoading = false;
        return [...response.distributions, ...response.result.map((fold) => ({
          ...fold,
          formattedName: `${fold.jobNumber} - ${fold.distributorName}`,
          formattedDistNum:`${fold.jobNumber} - ${fold.distributorNumber}`
        }))]
      })).pipe(catchError((error) => {
        console.log('Error fetching suggestions', error);
        Swal.fire({
          icon: 'error',
          title: '',
          text: 'Error Fetching Content'
        });
        return of([]);
      }));

    this.autoCompleteValue$.pipe(mergeMap((dataItem) => {
      this.gridDataLoading = true;
      this.gridData = [];
      return this.fileService.getFoldersForPath([...new Set([dataItem.distributorNumber, dataItem.jobNumber])], dataItem.getFromDb, dataItem.dataSource).pipe(catchError((err: any) => {
        console.error('Error Fetching folder content', err);
        Swal.fire({
          icon: 'error',
          title: '',
          text: `Error Fetching content for branch ${dataItem.distributorName}`
        });
        return of({
          result: []
        });
      }), map((response) => {
        return response.map((folder) => {
          folder.distributorName = dataItem.distributorName;
          folder.distributorNumber = dataItem.distributorNumber;
          return folder;
        });
      }));
    }))
      .subscribe((data: any) => {
        this.items = [];
        this.state = {...this.defaultState};
        this.gridData = [...this.gridData, ...data];
        this.distinctCategories = [...this.gridData];
        this.applyFilterAnimation('filterApplied');
        this.hardReloadSelected = false;
        this.gridDataLoading = false;
      }, () => {
        this.hardReloadSelected = false;
        this.gridDataLoading = false;
      });


    this.folderSelected$.pipe(mergeMap((folder) => {
      this.gridDataLoading = true;
      const path = this.items.map((item: any) => item.title);
      path.unshift(folder.distributorNumber);
      return this.fileService.getFoldersForPath([...new Set(path)], false, folder.dataSource).pipe(map((response) => {
        if(folder.dataSource === 2 && response.length === 0){
          Swal.fire({
            icon: 'warning',
            title: 'No data found.',
            text: `Pictures are archived, since the warranty period has elapsed. If you need the images please contact Administrator.`
          });
        }
        return response.map((detail) => {
          detail.distributorName = folder.distributorName;
          detail.distributorNumber = folder.distributorNumber;
          return detail;
        });
      }))
    })).subscribe((res) => {
      this.applyFilterAnimation('folderClicked');
      this.state = {...this.defaultState};
      this.gridData = Array.isArray(res) ? res : [];
      this.distinctCategories = [...new Set(this.gridData.map((data) => data.name))];
      this.gridDataLoading = false;
    }, () => {
      this.gridDataLoading = false;
    });


    this.gridData = [];

  }

  handleValue(selected: any) {
    if (selected && selected.length > 2) {
      this.autoCompleteSubject.next(selected);
    }
  }

  valueSelected(selected) {
    if (!selected) {
      this.items = [];
      this.gridData = [];
      this.autoCompleteSubject.next('');
      return;
    }
    this.selectedFolderSuggestion = selected;
    this.autoCompleteValueSubject.next(selected);
  }

  navigate(dataItem) {
    if(dataItem.dataSource == 1){
      if (dataItem.isFile) {
        dataItem.name.slice(-4).toUpperCase() === '.PDF' || '.PNG' || '.JPG' ? this.openFileInNewtab(dataItem) : '';
        return;
      }
    }
    else{
      if(dataItem.isFile){
        dataItem.name.slice(-4).toUpperCase() === '.PNG' || '.JPG' ? this.openFileInNewtab(dataItem) : '';
        return;
      }
    }
    if (this.items.length === 0) {
      const text = this.selectedFolderSuggestion.getFromDb ? this.selectedFolderSuggestion.distributorName : this.selectedFolderSuggestion.jobNumber;
      const title = this.selectedFolderSuggestion.getFromDb ? this.selectedFolderSuggestion.distributorNumber : this.selectedFolderSuggestion.jobNumber;
      this.items = [{text, title, icon: 'home'}];
    }
    const breadCrumbData = [...this.gridData].filter((ite) => (ite.distributorNumber === dataItem.distributorNumber && !ite.isFile));
    this.items.push({
      title: dataItem.name,
      text: dataItem.name,
      dropdownData: breadCrumbData,
      selectedValue: breadCrumbData.find((it) => it.name === dataItem.name)
    });
    this.gridData = [];
    this.items = [...this.items];
    this.folderSelected.next(dataItem);
  }

  onItemClick($event: any, index, item?: any) {
    item ? item.selectedValue = $event : '';
    this.gridData = [];
    if ($event.icon === 'home') {
      this.autoCompleteValueSubject.next(this.selectedFolderSuggestion);
    } else {
      const dat = {text: $event.name, title: $event.name};
      this.items.splice(index, 1, {...this.items[index], ...dat});
      this.items = this.items.slice(0, index + 1);
      this.folderSelected.next({
        'distributorName': this.items[index].selectedValue.distributorName,
        'distributorNumber': this.items[index].selectedValue.distributorNumber,
        'dataSource': this.items[index].selectedValue.dataSource
      });
    }
  }

  openFileInNewtab(dataItem) {
    const winRef = window.open();
    const fileURL = this.copyToClipBoard(dataItem);
    winRef.location.assign(fileURL);
  }

  downloadFile(dataItem, openInBrowser?: boolean) {
    this.isDownloading = true;
    let pathArray;
    if (this.items.length > 0) {
      pathArray = this.items.map((item: any) => item.title);
    } else {
      pathArray = [this.selectedFolderSuggestion.jobNumber];
    }
    this.fileService.downloadFile([...new Set([dataItem.distributorNumber, ...pathArray])], dataItem.name, openInBrowser, dataItem.dataSource,false).pipe(take(1)).subscribe((blob) => {
      importedSaveAs(blob, dataItem.name);
      this.isDownloading = false;
    }, () => {
      Swal.fire({
        icon: 'error',
        title: '',
        text: `Error Downloading file`
      });
      this.isDownloading = false;
    });
  }

  copyToClipBoard(dataItem) {
    let pathArray;
    if (this.items.length > 0) {
      pathArray = this.items.map((item: any) => item.title);
    } else {
      pathArray = [this.selectedFolderSuggestion.jobNumber];
    }
    pathArray = [...new Set([dataItem.distributorNumber, ...pathArray])];
    if(dataItem.dataSource == 1){
      return `${AppConsts.appBaseUrl}/download?storagePath=${encodeURIComponent(pathArray.join('/'))}&fileName=${encodeURIComponent(dataItem.name)}&dataSource=${encodeURIComponent(dataItem.dataSource)}`
    }
    else{
     return `${AppConsts.appBaseUrl}/download?storagePath=${encodeURIComponent(pathArray.join('/'))}&fileName=${encodeURIComponent(dataItem.name)}&dataSource=${encodeURIComponent(dataItem.dataSource)}`
    }
  }

  copiedEvent() {
    Swal.fire({
      title: '',
      timer: 1000,
      position: "top-right",
      html: 'Copied to clipboard'
    })
  }

  dataStateChange(state: DataStateChangeEvent) {
    this.state = state;
  }
}
